/**
 * Unscharfe genealogische Daten.
 *
 * Das Problem: In einem Stammbaum ist ein Datum selten ein Datum. Es ist
 * "1887", "um 1750", "vor der Hochzeit", "Maerz 1923". Die Altanwendung hat
 * das als int YYYYMMDD gespeichert und fehlende Teile auf 0 gesetzt
 * (19750000 = nur das Jahr bekannt). Das sortiert zwar, verliert aber die
 * Information, ob 1975 exakt oder geschaetzt ist.
 *
 * Neu: jedes Datum ist ein Intervall [von, bis] plus eine Angabe, wie es
 * gemeint war. Sortieren laeuft ueber `von`, Anzeigen ueber `genauigkeit`.
 * Ein exaktes Datum ist einfach ein Intervall der Laenge 1.
 */
export const GENAUIGKEITEN = [
    "exakt",
    "monat",
    "jahr",
    "um",
    "vor",
    "nach",
    "zeitraum",
];
const ZWEISTELLIG = (n) => String(n).padStart(2, "0");
const iso = (j, m, t) => `${String(j).padStart(4, "0")}-${ZWEISTELLIG(m)}-${ZWEISTELLIG(t)}`;
const letzterTag = (jahr, monat) => new Date(Date.UTC(jahr, monat, 0)).getUTCDate();
/** Wie unsicher "um 1900" ist: plus/minus so viele Jahre. */
export const UM_TOLERANZ_JAHRE = 5;
/**
 * Wandelt das alte int-Format YYYYMMDD in ein unscharfes Datum.
 * 0 bedeutet "unbekannt" und liefert null.
 * Fehlende Teile (Monat/Tag = 0) werden zum passenden Intervall aufgeweitet.
 */
export function ausAltFormat(zahl) {
    if (!zahl || zahl <= 0)
        return null;
    const tag = zahl % 100;
    const monat = Math.floor(zahl / 100) % 100;
    const jahr = Math.floor(zahl / 10000);
    if (jahr <= 0)
        return null;
    if (monat > 0 && monat <= 12 && tag > 0 && tag <= 31) {
        const d = iso(jahr, monat, tag);
        return { von: d, bis: d, genauigkeit: "exakt", text: null };
    }
    if (monat > 0 && monat <= 12) {
        return {
            von: iso(jahr, monat, 1),
            bis: iso(jahr, monat, letzterTag(jahr, monat)),
            genauigkeit: "monat",
            text: null,
        };
    }
    return {
        von: iso(jahr, 1, 1),
        bis: iso(jahr, 12, 31),
        genauigkeit: "jahr",
        text: null,
    };
}
/**
 * Parst eine Benutzereingabe. Bewusst grosszuegig, damit man in der Maske
 * einfach tippen kann, was in der Urkunde steht.
 *
 *   "12.03.1887"  "1887-03-12"  "3/1887"  "1887"
 *   "um 1750"     "ca 1750"     "vor 1900"   "nach 1900"
 *   "1914-1918"
 */
export function parseDatum(eingabe) {
    const roh = eingabe.trim();
    if (!roh)
        return null;
    const t = roh.toLowerCase().replace(/\s+/g, " ");
    const zeitraum = t.match(/^(\d{3,4})\s*(?:-|–|bis)\s*(\d{3,4})$/);
    if (zeitraum) {
        return {
            von: iso(Number(zeitraum[1]), 1, 1),
            bis: iso(Number(zeitraum[2]), 12, 31),
            genauigkeit: "zeitraum",
            text: roh,
        };
    }
    const praefix = t.match(/^(um|ca\.?|circa|etwa|vor|nach|seit)\s+(.*)$/);
    if (praefix) {
        const rest = parseDatum(praefix[2]);
        if (!rest)
            return null;
        const wort = praefix[1];
        if (wort === "vor")
            return { von: null, bis: rest.bis, genauigkeit: "vor", text: roh };
        if (wort === "nach" || wort === "seit")
            return { von: rest.von, bis: null, genauigkeit: "nach", text: roh };
        const jahr = Number(rest.von?.slice(0, 4) ?? 0);
        return {
            von: iso(jahr - UM_TOLERANZ_JAHRE, 1, 1),
            bis: iso(jahr + UM_TOLERANZ_JAHRE, 12, 31),
            genauigkeit: "um",
            text: roh,
        };
    }
    const dmy = t.match(/^(\d{1,2})[.\/-](\d{1,2})[.\/-](\d{3,4})$/);
    if (dmy) {
        const d = iso(Number(dmy[3]), Number(dmy[2]), Number(dmy[1]));
        return { von: d, bis: d, genauigkeit: "exakt", text: roh };
    }
    const ymd = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (ymd) {
        const d = iso(Number(ymd[1]), Number(ymd[2]), Number(ymd[3]));
        return { von: d, bis: d, genauigkeit: "exakt", text: roh };
    }
    const my = t.match(/^(\d{1,2})[.\/-](\d{3,4})$/);
    if (my) {
        const jahr = Number(my[2]);
        const monat = Number(my[1]);
        return {
            von: iso(jahr, monat, 1),
            bis: iso(jahr, monat, letzterTag(jahr, monat)),
            genauigkeit: "monat",
            text: roh,
        };
    }
    const y = t.match(/^(\d{3,4})$/);
    if (y) {
        const jahr = Number(y[1]);
        return { von: iso(jahr, 1, 1), bis: iso(jahr, 12, 31), genauigkeit: "jahr", text: roh };
    }
    // Nicht erkannt: Text erhalten, damit nichts verloren geht.
    return { von: null, bis: null, genauigkeit: "zeitraum", text: roh };
}
const MONATE = [
    "Jänner", "Februar", "März", "April", "Mai", "Juni",
    "Juli", "August", "September", "Oktober", "November", "Dezember",
];
/** Anzeige in der Oberflaeche. */
export function formatDatum(d) {
    if (!d)
        return "";
    const jahr = (s) => (s ? s.slice(0, 4) : "?");
    switch (d.genauigkeit) {
        case "exakt": {
            if (!d.von)
                return d.text ?? "";
            const [j, m, t] = d.von.split("-");
            return `${Number(t)}. ${MONATE[Number(m) - 1]} ${j}`;
        }
        case "monat": {
            if (!d.von)
                return d.text ?? "";
            const [j, m] = d.von.split("-");
            return `${MONATE[Number(m) - 1]} ${j}`;
        }
        case "jahr":
            return jahr(d.von);
        case "um":
            return `um ${Number(jahr(d.von)) + UM_TOLERANZ_JAHRE}`;
        case "vor":
            return `vor ${jahr(d.bis)}`;
        case "nach":
            return `nach ${jahr(d.von)}`;
        case "zeitraum":
            return d.text ?? `${jahr(d.von)}–${jahr(d.bis)}`;
    }
}
/**
 * Umkehrung von `parseDatum`: erzeugt den Text, den man in ein Eingabefeld
 * schreiben wuerde. Die Originaleingabe gewinnt, wenn es sie noch gibt -
 * damit bleibt "Ostern 1923" erhalten, auch wenn wir es nicht deuten konnten.
 */
export function alsEingabetext(d) {
    if (!d)
        return "";
    if (d.text)
        return d.text;
    if (!d.von && !d.bis)
        return "";
    const jahr = (s) => (s ? s.slice(0, 4) : "");
    const monat = (s) => Number(s.slice(5, 7));
    const tag = (s) => Number(s.slice(8, 10));
    switch (d.genauigkeit) {
        case "exakt":
            return d.von ? `${tag(d.von)}.${monat(d.von)}.${jahr(d.von)}` : "";
        case "monat":
            return d.von ? `${monat(d.von)}.${jahr(d.von)}` : "";
        case "jahr":
            return jahr(d.von);
        case "um":
            return d.von ? `um ${Number(jahr(d.von)) + UM_TOLERANZ_JAHRE}` : "";
        case "vor":
            return `vor ${jahr(d.bis)}`;
        case "nach":
            return `nach ${jahr(d.von)}`;
        case "zeitraum":
            return d.von && d.bis ? `${jahr(d.von)}-${jahr(d.bis)}` : jahr(d.von) || jahr(d.bis);
    }
}
/**
 * Ein Spaltenpaar (geburt_von, geburt_bis, ...) wieder als unscharfes Datum
 * lesen - die Umkehrung dessen, was in die Personenzeile geschrieben wird.
 * Steht hier, damit Frontend und Backend dieselbe Fassung benutzen.
 */
export function datumAusSpalten(p, art) {
    const von = art === "geburt" ? p.geburtVon : p.todVon;
    const bis = art === "geburt" ? p.geburtBis : p.todBis;
    const genauigkeit = art === "geburt" ? p.geburtGenauigkeit : p.todGenauigkeit;
    const text = art === "geburt" ? p.geburtText : p.todText;
    if (!von && !bis && !text)
        return null;
    return { von, bis, genauigkeit: genauigkeit ?? "jahr", text };
}
/**
 * Kurzform in Ziffern, fuer Listen: "30.11.1905", "11.1905", "1905",
 * "um 1750", "vor 1900", "1914-1918".
 *
 * Der Unterschied zu `formatDatum` ist die Breite: dort steht der Monat
 * ausgeschrieben, was in einer Trefferzeile neben zwanzig anderen keinen
 * Platz hat. Der Unterschied zu `alsEingabetext` ist, dass hier die
 * gespeicherten Grenzen zaehlen und nicht die Originaleingabe - "Ostern
 * 1923" taucht nur auf, wenn wir sonst gar nichts wissen.
 *
 * Entscheidend ist, dass die Genauigkeit mitgelesen wird: bei Lebenden
 * kuerzt der Server das Geburtsdatum auf das Jahr und setzt genauigkeit
 * "jahr". Wer stattdessen einfach `von` als Tag anzeigt, behauptet den
 * 1. Jaenner - ein erfundenes Datum, das nach Wahrheit aussieht.
 */
export function datumKurz(d) {
    if (!d)
        return "";
    const jahr = (s) => (s ? s.slice(0, 4) : "");
    const zahl = (s, von, bis) => Number(s.slice(von, bis));
    switch (d.genauigkeit) {
        case "exakt":
            return d.von ? `${zahl(d.von, 8, 10)}.${zahl(d.von, 5, 7)}.${jahr(d.von)}` : (d.text ?? "");
        case "monat":
            return d.von ? `${zahl(d.von, 5, 7)}.${jahr(d.von)}` : (d.text ?? "");
        case "jahr":
            return jahr(d.von) || (d.text ?? "");
        case "um":
            return d.von ? `um ${Number(jahr(d.von)) + UM_TOLERANZ_JAHRE}` : (d.text ?? "");
        case "vor":
            return d.bis ? `vor ${jahr(d.bis)}` : (d.text ?? "");
        case "nach":
            return d.von ? `nach ${jahr(d.von)}` : (d.text ?? "");
        case "zeitraum":
            if (d.von && d.bis)
                return `${jahr(d.von)}–${jahr(d.bis)}`;
            return jahr(d.von) || jahr(d.bis) || (d.text ?? "");
    }
}
/** Kurzform fuer die Baumdarstellung: nur das Jahr. */
export function jahrKurz(d) {
    if (!d)
        return "";
    if (d.genauigkeit === "um")
        return `~${Number((d.von ?? "0").slice(0, 4)) + UM_TOLERANZ_JAHRE}`;
    return (d.von ?? d.bis ?? "").slice(0, 4);
}
/** Grenze, ab der eine Person ohne Sterbedatum als verstorben gilt. */
export const MAX_LEBENSALTER = 100;
/**
 * Gilt die Person als lebend? Steuert, ob Details fuer Fremde sichtbar sind.
 *
 * Wie `giltAlsOeffentlich` wird mit `bis` gerechnet, dem spaetestmoeglichen
 * Datum. Fehlt es - kein Geburtsdatum oder ein nach oben offenes wie "nach
 * 1900" -, wird konservativ "lebend" angenommen, also geschuetzt. Mit `von`
 * zu rechnen waere hier der teurere Fehler: aus "nach 1900 geboren" wuerde
 * "126 Jahre alt, also tot", und ein Lebender gaebe Adresse und Notizen
 * preis.
 *
 * Ein Sterbeeintrag - und sei es nur ein Text - gilt als Auskunft: wer ihn
 * eingetragen hat, weiss, dass die Person tot ist.
 */
export function giltAlsLebend(geburt, tod) {
    if (tod)
        return false;
    const jahr = Number((geburt?.bis ?? "").slice(0, 4));
    if (!jahr)
        return true;
    return new Date().getUTCFullYear() - jahr < MAX_LEBENSALTER;
}
/** Wie lange ein Sterbefall nachwirkt, bevor der Datensatz offen ist. */
export const KARENZ_JAHRE = 25;
/**
 * Darf diese Person jedem angemeldeten Benutzer gezeigt werden?
 *
 * Nicht der Verwandtschaftsgrad entscheidet das, sondern die Zeit - so
 * halten es auch die anderen Programme (webtrees: "Alter, ab dem jemand als
 * verstorben gilt", Vorgabe 100 Jahre; GEDCOM kennt dafuer das Merkmal
 * RESN). Ein Urgrossvater von 1880 ist keine Privatsache mehr, ein lebender
 * Schwager schon.
 *
 * Zwei Wege dorthin:
 *   - vor mehr als MAX_LEBENSALTER Jahren geboren, oder
 *   - vor mehr als KARENZ_JAHRE Jahren gestorben.
 *
 * Die Karenz ist der Unterschied zu "einfach jeder Verstorbene": ein
 * kuerzlich gestorbener Onkel bleibt hinter der Verwandtschaftsgrenze.
 * Ohne jedes Datum bleibt eine Person geschuetzt - Unwissen ist kein Grund,
 * etwas freizugeben.
 *
 * Gerechnet wird ausschliesslich mit `bis`, dem spaetestmoeglichen Datum.
 * Fehlt es, ist das Datum nach oben offen ("nach 1900") - dann kann die
 * Person von gestern sein, und es bleibt beim Schutz. `von` waere hier die
 * falsche Ersatzangabe: aus "nach 1900 geboren" wuerde sonst "vor ueber
 * hundert Jahren geboren", und ein Lebender stuende offen da.
 */
export function giltAlsOeffentlich(geburt, tod, karenzJahre = KARENZ_JAHRE, heute = new Date()) {
    const jetzt = heute.getUTCFullYear();
    const todJahr = Number((tod?.bis ?? "").slice(0, 4));
    if (todJahr && jetzt - todJahr > karenzJahre)
        return true;
    // "um 1930" heisst hier 1935 - erst dann ist die Frist sicher um.
    const geburtJahr = Number((geburt?.bis ?? "").slice(0, 4));
    if (geburtJahr && jetzt - geburtJahr > MAX_LEBENSALTER)
        return true;
    return false;
}
/** Volle Jahre zwischen zwei ISO-Tagen. */
function jahreZwischen(von, bis) {
    const j = Number(bis.slice(0, 4)) - Number(von.slice(0, 4));
    // Monat und Tag als eine Zahl vergleichen: 0312 < 0705.
    const tagImJahr = (s) => Number(s.slice(5, 7)) * 100 + Number(s.slice(8, 10));
    return tagImJahr(bis) < tagImJahr(von) ? j - 1 : j;
}
export function lebensalter(geburt, tod, giltAlsLebend, heute = new Date()) {
    if (!geburt || (!geburt.von && !geburt.bis))
        return null;
    let endeFrueh;
    let endeSpaet;
    let laufend = false;
    if (tod && (tod.von || tod.bis)) {
        // KEIN Rueckfall auf die jeweils andere Grenze: bei "gestorben vor
        // 1900" ist nur das spaeteste Ende bekannt, das fruehestmoegliche
        // liegt am Tag der Geburt. Wer hier `bis` einsetzt, macht aus einer
        // offenen Spanne eine Zahl - "50 Jahre" fuer jemanden, der mit einem
        // Jahr gestorben sein kann. In einer Chronik ist "vor/nach <Jahr>"
        // der Normalfall, wenn erst ein spaeterer Eintrag jemanden als
        // verstorben nennt.
        endeFrueh = tod.von;
        endeSpaet = tod.bis;
    }
    else if (giltAlsLebend) {
        endeFrueh = endeSpaet = heute.toISOString().slice(0, 10);
        laufend = true;
    }
    else {
        // Verstorben, aber wann - unbekannt. Dann gibt es kein Alter.
        return null;
    }
    const rohMin = geburt.bis && endeFrueh ? jahreZwischen(geburt.bis, endeFrueh) : null;
    const max = geburt.von && endeSpaet ? jahreZwischen(geburt.von, endeSpaet) : null;
    /*
     * Erst die Obergrenze entscheidet, ob die Daten einander widersprechen.
     * Ein negatives MINIMUM ist dagegen voellig in Ordnung und haeufig: bei
     * "1920 geboren, 1920 gestorben" wird der spaeteste Geburtstag
     * (31.12.) gegen den fruehesten Tod (1.1.) gerechnet, und das ergibt
     * -1. Gemeint ist: die Person kann bei der Geburt gestorben sein, also
     * null Jahre. Wer hier das ganze Ergebnis verwirft, zeigt beim
     * Saeugling und bei jedem "um 1900 / um 1905" gar kein Alter.
     */
    if (max !== null && max < 0)
        return null;
    const min = rohMin === null ? null : Math.max(0, rohMin);
    if (min === null && max === null)
        return null;
    return { min, max, laufend };
}
/** „73 Jahre", „etwa 73 Jahre", „mindestens 61 Jahre", „ein Jahr". */
export function altersText(a) {
    if (!a)
        return "";
    const jahre = (n) => (n === 1 ? "ein Jahr" : `${n} Jahre`);
    if (a.min !== null && a.max !== null) {
        if (a.min === a.max)
            return jahre(a.min);
        // Die Mitte, nicht die Obergrenze: bei jahrgenauen Daten liegen min
        // und max genau ein Jahr auseinander, und beide sind gleich wahr.
        return `etwa ${jahre(Math.round((a.min + a.max) / 2))}`;
    }
    if (a.min !== null)
        return `mindestens ${jahre(a.min)}`;
    return `höchstens ${jahre(a.max)}`;
}
//# sourceMappingURL=datum.js.map